export class Report {
  readonly logsRootDirPromise: Promise<FileSystemDirectoryHandle>;

  private currentDateFileHandler: FileSystemFileHandle | undefined;
  private LOGS_ROOT_DIR = 'halo-chat-logs';
  private MAX_PART_SIZE_BYTES = 10 * 1024 * 1024;

  constructor() {
    this.logsRootDirPromise = navigator.storage.getDirectory().then((root) =>
      root.getDirectoryHandle(this.LOGS_ROOT_DIR, {
        create: true,
      })
    );
  }

  get currentDateFolderPath() {
    return this.getDateFolderPath(new Date());
  }

  get currentDateFolder() {
    return this.logsRootDirPromise.then((root) =>
      root.getDirectoryHandle(this.currentDateFolderPath, { create: true })
    );
  }

  getDateFolderPath(date: Date) {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  async append(text: string) {
    const currentDateFolderHandle = await this.currentDateFolder;

    let index = 0;

    if (!this.currentDateFileHandler) {
      for await (const [name, handle] of currentDateFolderHandle.entries()) {
        const index_ = Number(name);
        if (index <= index_) {
          index = index_;
          this.currentDateFileHandler = handle as FileSystemFileHandle;
        }
      }

      if (!this.currentDateFileHandler) {
        this.currentDateFileHandler = await currentDateFolderHandle.getFileHandle('0', {
          create: true,
        });
      }
    }

    const file = await this.currentDateFileHandler.getFile();

    const bytes = new TextEncoder().encode(text);

    const writable = await this.currentDateFileHandler.createWritable({ keepExistingData: true });
    await writable.seek(file.size);
    await writable.write(bytes);
    await writable.close();

    // Rotate part if the next write would exceed the threshold.

    if (file.size > this.MAX_PART_SIZE_BYTES) {
      index++;
      this.currentDateFileHandler = await currentDateFolderHandle.getFileHandle(
        index.toString(),
        {
          create: true,
        }
      );
    }
  }

  async export(date?: Date) {
    const logsRootDirHandler = await this.logsRootDirPromise;
    const dateFolderPath = date ? this.getDateFolderPath(date) : this.currentDateFolderPath;

    const dateFolderHandle = await logsRootDirHandler.getDirectoryHandle(dateFolderPath);

    const files: File[] = [];

    for await (const [, handle] of dateFolderHandle.entries()) {
      if (handle?.kind === 'file') {
        files.push(await handle.getFile());
      }
    }

    return files;
  }

  async cleanupOldDateFolders() {
    const { currentDateFolderPath } = this;
    const logsRootDirHandler = await this.logsRootDirPromise;
    for await (const [name, handle] of logsRootDirHandler.entries()) {
      if (handle?.kind === 'directory' && name < currentDateFolderPath) {
        await this.clearDir(handle);
      }
    }
  }

  async clearDir(dir: FileSystemDirectoryHandle) {
    await (async function clear(entry) {
      for await (const value of (entry as any).values()) {
        if (value.kind === 'directory') {
          await clear(value);
        }
        await value.remove();
      }
      await (entry as any).remove();
    })(dir);
  }
}

function safeJsonStringify(value: unknown) {
  const seen = new WeakSet<object>();
  return JSON.stringify(value, (_k, v) => {
    if (typeof v === 'bigint') return v.toString();
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      const objectName = Object.prototype.toString.call(v);
      if (objectName !== '[object Object]' && objectName !== '[object Array]') {
        return objectName;
      }
      seen.add(v);
    }
    return v;
  });
}

export function toJsonlLine(payload: unknown) {
  if (typeof payload === 'object') {
    return `${safeJsonStringify(payload)}\n`;
  }
  return String(payload);
}
